import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import React, { useMemo, useState } from "react";

const getGroupLabel = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const midnightNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const midnightDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const diffDays = Math.floor((midnightNow - midnightDate) / 86400000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays <= 7) return "Previous 7 Days";
  return "Older";
};

const groupOrder = ["Today", "Yesterday", "Previous 7 Days", "Older"];

const SherpaSidebar = ({
  sessions,
  selectedSessionId,
  isLoadingSessions,
  sessionsError,
  hasInterruptedSession,
  isOngoing = false,
  hideHeader = false,
  onSelectSession,
  onResumeInterrupted,
  onNewSession,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const groupedSessions = useMemo(() => {
    const grouped = sessions.reduce((acc, session) => {
      const key = getGroupLabel(session.updatedAt || session.createdAt);
      if (!acc[key]) acc[key] = [];
      acc[key].push(session);
      return acc;
    }, {});
    return groupOrder.filter((key) => grouped[key]?.length).map((key) => ({ label: key, sessions: grouped[key] }));
  }, [sessions]);

  return (
    <Box sx={{ p: 2, overflowY: "auto", height: "100%" }}>
      {!hideHeader && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="h6" sx={{ px: 0.5, fontWeight: 750, color: "#164A38" }}>
            Session History
          </Typography>
          <IconButton
            aria-label="Start a new session"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            size="small"
            sx={{ color: "#0C8F63" }}
          >
            <AddRoundedIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 180 } } }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onNewSession?.();
              }}
            >
              <AddRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Start new session
            </MenuItem>
          </Menu>
        </Box>
      )}
      {hasInterruptedSession ? (
        <ListItemButton
          onClick={onResumeInterrupted}
          disabled={isOngoing || isLoadingSessions}
          sx={{ mb: 1.5, borderRadius: 3, bgcolor: "#EAF8F1", color: "#176345" }}
        >
          <ListItemText primary="Resume your last session" />
          <ChevronRightRoundedIcon />
        </ListItemButton>
      ) : null}
        {isLoadingSessions ? (
          <>
            <Skeleton height={28} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </>
        ) : groupedSessions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            No conversations yet.
          </Typography>
        ) : (
          groupedSessions.map((group) => (
            <Box key={group.label} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 1.25, display: "block", mb: 0.5 }}
              >
                {group.label}
              </Typography>
              <List disablePadding>
                {group.sessions.map((session) => (
                  <ListItemButton
                    key={session.id}
                    disabled={isOngoing}
                    selected={selectedSessionId === session.id}
                    onClick={() => !isOngoing && onSelectSession(session.id)}
                    sx={{
                      border: "1px solid",
                      borderColor: selectedSessionId === session.id ? "#AEE3C8" : "#E0ECE5",
                      borderRadius: 3,
                      mb: 1,
                      px: 1.75,
                      py: 1.25,
                      bgcolor: selectedSessionId === session.id ? "#EFFAF4" : "#FFFFFF",
                    }}
                  >
                    <ListItemText
                      primary={session.isArchived ? `Session #${session.dbSessionId}` : session.title || "New Chat"}
                      secondary={`${session.messageCount ?? session.messages?.length ?? 0} messages • ${new Date(session.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`}
                      primaryTypographyProps={{ fontWeight: 700, color: "#1A4C3B" }}
                      secondaryTypographyProps={{ sx: { mt: 0.4, color: "#71867B" } }}
                    />
                    <ChevronRightRoundedIcon sx={{ color: "#5A8874" }} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))
        )}
        {sessionsError ? (
          <Typography variant="body2" role="alert" sx={{ color: "error.main", px: 1 }}>
            {sessionsError}
          </Typography>
        ) : null}
    </Box>
  );
};

export default SherpaSidebar;
